/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PRISM_SPACING_1000 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralOverlayWithMessage from 'lumen/components/general/overlay-with-message';
import ParityTooltipsTooltipTrends, {
  type ViolatorData,
} from 'lumen/components/parity/tooltips/tooltip-trends';
import type { ParityInsightSegmentationItem } from 'lumen/models';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';
import {
  type Breakdown,
  type DateAggregation,
  PA_SORT_TYPES,
  type ParityFilter,
  SORT_TYPES,
  type SortType,
} from 'lumen/utils/constants/pi-constant-values';
import { parityAnalyticsDateFormat } from 'lumen/utils/parity-analytics/date-utils';

interface TrendDataPoint {
  crossed?: boolean;
  dashed?: boolean;
  extractDate: string;
  violator: ParityInsightSegmentationItem;
}

interface TrendSeries {
  dataPoints: TrendDataPoint[];
  item: ParityInsightSegmentationItem;
}

interface TrendPoint {
  marker: typeof HIGHCHARTS_MISSING_MARKER | undefined;
  violator: ParityInsightSegmentationItem;
  x: number;
  y: number | null | undefined;
}

type Trends = TrendPoint[] & { hasNothingToDisplay?: boolean };

type TrendChartSeries = Omit<Highcharts.SeriesLineOptions, 'data'> & {
  data: Trends;
};

interface ParityChartsChartTrendsSignature {
  Args: {
    data: TrendSeries[];
    dateAggregation: DateAggregation;
    height: number;
    isLoading: boolean;
    segmentation: Breakdown | null;
    sortType: SortType;
    winMeetOrLoss: ParityFilter;
  };
}

export default class ParityChartsChartTrends extends Component<ParityChartsChartTrendsSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];

  declare displayName: string;
  declare violatorData: ViolatorData[];

  get chartData(): TrendChartSeries[] {
    const chartData: TrendChartSeries[] = [];

    this.args.data.forEach((series) => {
      const [data, zones] = this.createTrends(series.dataPoints);

      chartData.push({
        name: series.item.formattedSegmentationValue,
        zoneAxis: 'x',
        type: 'line',
        data,
        zones,
      });
    });

    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    let enableLegend = false;

    if (!this.hasNoData) {
      enableLegend = true;
    }

    return {
      chart: {
        type: 'line',
        marginTop: PRISM_SPACING_1000,
        zoomType: null,
        height: this.args.height,
      },
      title: {
        text: 'parity-trends-chart',
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: enableLegend,
        borderWidth: 0,
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          width: 200,
          fontSize: 12,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,

        formatter(this: Highcharts.TooltipFormatterContextObject): string {
          const { chart } = this.series;
          const { series } = chart;
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          const formattedExtractDate = parityAnalyticsDateFormat(
            dayjs.unix(this.x / 1000).utc(),
            that.args?.dateAggregation,
          );
          const formattedExtractDateEndOfWeek = parityAnalyticsDateFormat(
            dayjs
              .unix(this.x / 1000)
              .add(6, 'day')
              .utc(),
            that.args?.dateAggregation,
          );
          let displayName = `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`;

          if (that.args?.dateAggregation === 'month') {
            displayName = formattedExtractDate;
          }

          set(
            that,
            'violatorData',
            series.map((serie) => {
              const point = that.findPointByXValue(serie, this.point.x);
              // @ts-expect-error: Property 'color' does not exist on type 'Point | undefined'.
              const { color } = point;

              return {
                // @ts-expect-error: 'point' is possibly 'undefined'.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data: point.violator,
                name: serie.name,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                color,
                // @ts-expect-error: Property 'violator' does not exist on type 'Point'.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                selected: this.point.violator.id === point.violator.id,
              };
            }),
          );
          set(that, 'displayName', displayName);
          end();
          return (
            document.getElementById('dummy-tooltip-for-trends')?.innerHTML ?? ''
          );
        },
        useHTML: true,
      },
      xAxis: {
        allowDecimals: false,
        type: 'datetime',

        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            const dateAggregation = that.args?.dateAggregation;
            const formattedExtractDate = parityAnalyticsDateFormat(
              // @ts-expect-error: The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
              dayjs.unix(this.value / 1000).utc(),
              dateAggregation,
            );

            if (dateAggregation === 'month') {
              return formattedExtractDate;
            }

            const formattedExtractDateEndOfWeek = parityAnalyticsDateFormat(
              dayjs
                // @ts-expect-error: The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
                .unix(this.value / 1000)
                .add(6, 'day')
                .utc(),
              dateAggregation,
            );
            return `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`;
          },
        },
      },
      yAxis: {
        allowDecimals: that.args.sortType === SORT_TYPES.RELATIVE,
        endOnTick: true,
        ordinal: false,
        showFirstLabel: false,
        min: 0,
        ceiling: that.args.sortType === SORT_TYPES.RELATIVE ? 1 : null,
        title: {
          text: '',
        },
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            if (that.args.sortType === SORT_TYPES.RELATIVE) {
              // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number | bigint | StringNumericLiteral | null | undefined'.
              return that.intl.formatNumber(this.value, { style: 'percent' });
            }

            return `${this.value}`;
          },
        },
      },
      plotOptions: {
        series: {
          lineWidth: 2,
        },
      },
    };
  }

  get hasNoData(): boolean {
    return (
      !this.args.isLoading &&
      this.chartData.every((series) => series.data.hasNothingToDisplay)
    );
  }

  createTrends(
    violators: TrendDataPoint[],
  ): [Trends, Highcharts.SeriesZonesOptionsObject[]] {
    // Making sure we set x to 0 for the zones not to get undefined values
    const previousPoint: {
      hasNoData: boolean | undefined;
      x: number;
      y: number | null | undefined;
    } = { x: 0, y: null, hasNoData: false };
    const zones: Highcharts.SeriesZonesOptionsObject[] = [];

    const sortType = PA_SORT_TYPES[this.args.sortType];

    // Flag data as missing when point are null so we don't ahave to re-iterate over it later on
    let hasNothingToDisplay = true;

    // Remember that violatorData consists of an extractDate and a violator object as made in route::invertViolatorsAndDates
    // violatorData = {
    //   violator1: [{extractDate: "2018-03-01", violator: segmentationValue}, ...](n=weeks),
    //   ...
    // }
    const data: Trends = violators.map((violatorData, index, allViolators) => {
      const isLastViolator = allViolators.length - 1 === index;
      const x = this.xValue(violatorData.extractDate);
      let y: number | null | undefined =
        violatorData.violator[sortType]?.[this.args.winMeetOrLoss];
      let marker;

      // Check if current dataPoint has number of shops
      if (violatorData.violator.hasNoData) {
        violatorData.crossed = true;

        // Missing points always extend the previous y value
        y = previousPoint.y;
      } else {
        hasNothingToDisplay = false;
      }

      if (previousPoint.hasNoData) {
        violatorData.dashed = true;
      }

      if (violatorData.dashed) {
        zones.push({
          value: previousPoint.x,
          dashStyle: 'Dot',
        });

        // If this is the last point, there will be no next point to set the zones on this point
        // So this sets a dotted zone for itself as the last point (else we have a solid segment in some cases where there is no data)
        if (isLastViolator) {
          zones.push({
            value: x,
            dashStyle: 'Dot',
          });
        }
      } else {
        zones.push({
          value: previousPoint.x,
          dashStyle: 'Solid',
        });
      }

      if (violatorData.crossed) {
        marker = HIGHCHARTS_MISSING_MARKER;
      }

      // Remember point for padding in future potential missing data
      previousPoint.y = y;
      previousPoint.x = x;
      previousPoint.hasNoData = violatorData.violator.hasNoData;

      return {
        x,
        y,
        violator: violatorData.violator,
        marker,
      };
    });

    data.hasNothingToDisplay = hasNothingToDisplay;

    return [data, zones];
  }

  findPointByXValue(
    serie: Highcharts.Series,
    xValue: number,
  ): Highcharts.Point | undefined {
    let point;
    serie.points.some((currentPoint) => {
      if (currentPoint.x === xValue) {
        point = currentPoint;
        return true;
      }

      return false;
    });

    return point;
  }

  xValue(timestamp: string): number {
    return dayjs.utc(timestamp).unix() * 1000;
  }

  <template>
    <div class="relative">
      <GeneralChartsOtaChart
        {{! @glint-expect-error: Type '{ chart: { type: string; marginTop: number; zoomType: null; ... }; ... }' is not assignable to type 'Options'. }}
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
      />
      {{#if this.hasNoData}}
        <GeneralOverlayWithMessage @message={{t "general.no_data"}} />
      {{/if}}
    </div>
    <div class="parity-tooltip hidden" id="dummy-tooltip-for-trends">
      <ParityTooltipsTooltipTrends
        @displayName={{this.displayName}}
        @key={{@winMeetOrLoss}}
        @segmentation={{@segmentation}}
        @sortType={{@sortType}}
        @violatorData={{this.violatorData}}
      />
    </div>
  </template>
}
